import React, { useEffect, useMemo, useState, KeyboardEvent, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import debounce from 'lodash-es/debounce';
import {
  AppointmentTypeFragment,
  useUpdateOneAppointmentTypeMutation,
  useUpsertPetPortalSettingsMutation,
  useUpdateAppointmentTypeOrderMutation,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import HasFeature from 'shared/components/HasFeature';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { SortableList, SortableItem, DragHandle } from '@televet/kibble-ui/build/components/SortableList';
import { Divider, Box, Flex, Center, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import EditAppointmentTypeModal from './EditModal/index';
import { useAppointmentSettings } from '../../hooks/useAppointmentSettings';

enum ToggleSettingName {
  ShowInPetPortal = 'showInPetPortal',
  IsAvailableForDirectBooking = 'isAvailableForDirectBooking',
}

const AppointmentTypeSettings = ({ hasPermission }: { hasPermission?: boolean }): JSX.Element => {
  const { currentClinic } = useClinicUser();
  const clinicSettingId = currentClinic?.clinicSetting?.id ?? '';
  const { apptTypes, areAppointmentSettingsLoading, showAppointmentsInPetPortal } = useAppointmentSettings();
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeFragment[]>([]);
  useEffect(() => setAppointmentTypes(apptTypes), [apptTypes]);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedApptType, setSelectedApptType] = useState<AppointmentTypeFragment>();

  const toast = useToast();
  const { isOpen: isApptTypeModalOpen, onOpen: onApptTypeModalOpen, onClose: onApptTypeModalClose } = useDisclosure();

  const [upsertPetPortalSettings] = useUpsertPetPortalSettingsMutation();

  const [updateAppointmentType] = useUpdateOneAppointmentTypeMutation({
    refetchQueries: ['getAppointmentSettings'],
  });

  const [updateAppointmentTypeOrder] = useUpdateAppointmentTypeOrderMutation();

  const handleSearchTextChange = useMemo(
    () =>
      debounce(
        (searchText): void => {
          const text = searchText.trim().toLowerCase();
          setDebouncedSearchText(text);
        },
        200,
        { leading: false, trailing: true },
      ),
    [setDebouncedSearchText],
  );

  useEffect(() => {
    handleSearchTextChange(searchText);
  }, [searchText, handleSearchTextChange]);

  const handleSearchInputKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      setSearchText('');
    }
  };

  const filteredTypes = useMemo(() => {
    if (!debouncedSearchText) {
      return appointmentTypes;
    }

    return appointmentTypes.filter(
      (type) =>
        type.name.toLowerCase().includes(debouncedSearchText) ||
        type.displayName?.toLowerCase()?.includes(debouncedSearchText),
    );
  }, [appointmentTypes, debouncedSearchText]);

  const handleAppointmentTypeSettingToggled = useCallback(
    async (appointmentTypeId: string, settingName: ToggleSettingName, isEnabled: boolean) => {
      try {
        await updateAppointmentType({
          variables: {
            where: {
              id: appointmentTypeId,
            },
            data: {
              [settingName]: !isEnabled,
            },
          },
        });

        toast({ title: 'Appointment type successfully updated.', status: 'success' });
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        toast({
          title: 'Something went wrong saving changes to the appointment type. Please try again.',
          status: 'error',
        });
      }
    },
    [updateAppointmentType, toast],
  );

  const handlePetPortalAppointmentsToggled = useCallback(
    async (isEnabled: boolean): Promise<void> => {
      try {
        await upsertPetPortalSettings({
          variables: {
            where: {
              clinicSettingId,
            },
            create: {
              clinicSetting: {
                connect: {
                  id: clinicSettingId,
                },
              },
              showAppointments: !isEnabled,
            },
            update: {
              showAppointments: !isEnabled,
            },
          },
        });

        toast({ title: 'Show Appointments in Pet Portal setting successfully updated.', status: 'success' });
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        toast({
          title:
            'Something went wrong when toggling the Show Appointments in Pet Portal setting. Please refresh and try again.',
          status: 'error',
        });
      }
    },
    [clinicSettingId, toast, upsertPetPortalSettings],
  );

  const ProviderCountIndicator = ({
    numOfProviders,
    isAvailableForDirectBooking,
  }: {
    numOfProviders: number;
    isAvailableForDirectBooking: boolean;
  }): JSX.Element => {
    const showWarning = numOfProviders === 0 && isAvailableForDirectBooking;
    return (
      <Text size="sm" align="center" variant={showWarning ? 'danger' : 'default'}>
        {numOfProviders}
        {showWarning && (
          <Tooltip
            label="Appointments will not be available for direct booking until at least one provider is added."
            titleCaseLabel={false}
          >
            <Box p={1} verticalAlign="middle" display="inline">
              <Icon size="xs" name="warningSign" variant="danger" />
            </Box>
          </Tooltip>
        )}
      </Text>
    );
  };

  const handleOpenEditModal = (appointmentType: AppointmentTypeFragment): void => {
    setSelectedApptType(appointmentType);
    onApptTypeModalOpen();
  };

  const handleCloseEditModal = (): void => {
    setSelectedApptType(undefined);
    onApptTypeModalClose();
  };

  const handleListSort = (types: AppointmentTypeFragment[]): void => {
    setAppointmentTypes(types);

    const data = types.map((type, index) => {
      return {
        id: type.id,
        displayOrder: index,
      };
    });

    updateAppointmentTypeOrder({ variables: { data } });
  };

  return (
    <Box pb={10}>
      <EditAppointmentTypeModal
        isOpen={isApptTypeModalOpen}
        onClose={handleCloseEditModal}
        appointmentType={selectedApptType}
      />

      <Box my={4} maxW="700px">
        <Text size="sm" variant="subtle">
          Set which appointment types will show to clients in the Pet Portal as well as their display names.
          <HasFeature name={FeatureFlagName.DirectBooking}>
            {` You can also set which appointment types will be available in your website widget if using Direct Booking.`}
          </HasFeature>
        </Text>
      </Box>
      <Divider />
      <Box my={4} maxW="700px">
        <Switch
          size="sm"
          label="Show appointments in Pet Portal"
          labelStyle={{ fontSize: 'sm', fontWeight: 'semibold' }}
          isChecked={showAppointmentsInPetPortal}
          isFieldInline
          onChange={async (): Promise<void> => await handlePetPortalAppointmentsToggled(showAppointmentsInPetPortal)}
        />
        <Text as="p" size="sm" variant="subtle">
          When switched on, previous and upcoming appointments will be shown to clients in the Pet Portal. The types of
          appointments that show can be controlled individually in the table below.
        </Text>
      </Box>
      <Divider />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={10}>
          <Heading size="sm">Appointment Types</Heading>
          <TextInput
            size="md"
            leftElement={<Icon size="sm" name="magnifyingGlass" />}
            formControlStyle={{ width: '30%' }}
            placeholder="Search appointment types..."
            onChange={(e): void => setSearchText(e.target.value)}
            isClearable
            onClear={(): void => setSearchText('')}
            onKeyDown={handleSearchInputKeyDown}
          />
        </Flex>
        {areAppointmentSettingsLoading && !filteredTypes.length ? (
          <Center>
            <Spinner size="lg" />
          </Center>
        ) : (
          <Box>
            <Flex justifyContent="space-between" alignItems="center">
              <Box display="inline-block" w={12} />
              <Text size="xs" w="20%" pb={2} pl={3}>
                APPOINTMENT TYPE
              </Text>
              <Text size="xs" w="20%" pb={2}>
                DISPLAY NAME
              </Text>
              <Text size="xs" w="15%" pb={2} align="center">
                SHOW IN PET PORTAL
              </Text>
              <HasFeature name={FeatureFlagName.DirectBooking}>
                <Text size="xs" w="15%" pb={2} align="center">
                  DIRECT BOOKING
                </Text>
                <Text size="xs" w="10%" pb={2} align="center">
                  DURATION (MIN)
                </Text>
                <Text size="xs" w="10%" pb={2} align="center">
                  PROVIDERS
                  <Tooltip
                    label="Clients can choose from these providers when booking an appointment directly."
                    titleCaseLabel={false}
                  >
                    <Box p={1} verticalAlign="middle" display="inline">
                      <Icon size="xs" name="infoCircle" variant="subtle" />
                    </Box>
                  </Tooltip>
                </Text>
              </HasFeature>
              {hasPermission && (
                <Text size="xs" w="10%" pb={2} align="center">
                  EDIT
                </Text>
              )}
            </Flex>
            <Divider mb={5} />
            {debouncedSearchText && !filteredTypes.length && (
              <Center>
                <Text mt={6}>{`No results found for "${debouncedSearchText}"`}</Text>
              </Center>
            )}
            <SortableList
              items={filteredTypes}
              onChange={(filteredTypes): void => handleListSort(filteredTypes)}
              renderItem={(type, index): JSX.Element => {
                const {
                  name,
                  displayName,
                  id,
                  showInPetPortal,
                  isAvailableForDirectBooking,
                  defaultDurationInMinutes,
                  clinicEmployeeAppointmentTypeSettings,
                } = type;

                return (
                  <SortableItem id={id}>
                    <Flex
                      key={id}
                      alignItems="center"
                      justifyContent="space-between"
                      p={2}
                      bgColor={(index && index % 2 === 0) || index === 0 ? 'background.default' : 'transparent'}
                    >
                      {hasPermission && <DragHandle />}
                      <Text size="sm" w="20%" pl={3}>
                        {name}
                      </Text>
                      <Text size="sm" w="20%">
                        {displayName}
                      </Text>
                      <Flex w="15%" alignItems="center" justifyContent="center">
                        <Switch
                          size="sm"
                          isDisabled={!hasPermission}
                          isChecked={showInPetPortal}
                          onChange={async (): Promise<void> =>
                            await handleAppointmentTypeSettingToggled(
                              id,
                              ToggleSettingName.ShowInPetPortal,
                              showInPetPortal,
                            )
                          }
                          formControlStyle={{ width: 'max-content' }}
                        />
                      </Flex>
                      <HasFeature name={FeatureFlagName.DirectBooking}>
                        <Flex w="15%" alignItems="center" justifyContent="center">
                          <Switch
                            size="sm"
                            isDisabled={!hasPermission}
                            isChecked={isAvailableForDirectBooking}
                            onChange={async (): Promise<void> =>
                              await handleAppointmentTypeSettingToggled(
                                id,
                                ToggleSettingName.IsAvailableForDirectBooking,
                                isAvailableForDirectBooking,
                              )
                            }
                            formControlStyle={{ width: 'max-content' }}
                          />
                        </Flex>
                        <Text size="sm" w="10%" align="center">
                          {defaultDurationInMinutes}
                        </Text>
                        <Flex alignItems="center" justifyContent="center" w="10%">
                          <ProviderCountIndicator
                            numOfProviders={clinicEmployeeAppointmentTypeSettings.length}
                            isAvailableForDirectBooking={isAvailableForDirectBooking}
                          />
                        </Flex>
                      </HasFeature>
                      {hasPermission && (
                        <Center width="10%">
                          <Button
                            iconName="pen"
                            variant="ghostNeutral"
                            onClick={(): void => handleOpenEditModal(type)}
                          />
                        </Center>
                      )}
                    </Flex>
                  </SortableItem>
                );
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AppointmentTypeSettings;
